import {
  compileSolanaMessage,
  solanaComputeUnitLimitInstruction,
  solanaComputeUnitPriceInstruction,
} from "@stealthos/sdk";
import { ethers } from "ethers";
import { getSolanaLatestBlockhash, type SolanaRpc } from "./rpc";

export const SOLANA_DEPOSIT_MAX_COMPUTE_UNITS = 1_400_000;
export const SOLANA_DEPOSIT_COMPUTE_UNIT_PRICE = 1_000n;
// Anchor discriminator + u32 tree + u32 leaf + u64 slot, matching the pool.
const COMMITMENT_RECORD_BYTES = 56;
// RecoveryRecord header (78) plus the compact complete-note envelope (143).
const RECOVERY_RECORD_BYTES = 221;
const FEE_BUFFER_LAMPORTS = 10_000n;

export interface SolanaDepositFees {
  networkFee: bigint;
  accountRent: bigint;
  buffer: bigint;
  reserve: bigint;
}

function lamports(value: unknown, name: string): bigint {
  if (typeof value !== "number" || !Number.isSafeInteger(value) || value <= 0) {
    throw new Error(`Could not estimate ${name}. Try again.`);
  }
  return BigInt(value);
}

/** Quote a one-signer message at the compute cap; no note or proof is disclosed. */
export async function estimateSolanaDepositFees(
  rpc: SolanaRpc,
  payer: string,
  options: { depositMerge?: boolean; onchainRecovery?: boolean } = {},
): Promise<SolanaDepositFees> {
  const [blockhash, rent, recoveryRent, nullifierRent] = await Promise.all([
    getSolanaLatestBlockhash(rpc),
    rpc.request<number>("getMinimumBalanceForRentExemption", [
      options.onchainRecovery ? COMMITMENT_RECORD_BYTES : 24,
      { commitment: "confirmed" },
    ]),
    options.onchainRecovery
      ? rpc.request<number>("getMinimumBalanceForRentExemption", [
          RECOVERY_RECORD_BYTES,
          { commitment: "confirmed" },
        ])
      : Promise.resolve(null),
    options.depositMerge
      ? rpc.request<number>("getMinimumBalanceForRentExemption", [
          0,
          { commitment: "confirmed" },
        ])
      : Promise.resolve(null),
  ]);
  const message = compileSolanaMessage({
    payer,
    recentBlockhash: blockhash.blockhash,
    instructions: [
      solanaComputeUnitLimitInstruction(SOLANA_DEPOSIT_MAX_COMPUTE_UNITS),
      solanaComputeUnitPriceInstruction(SOLANA_DEPOSIT_COMPUTE_UNIT_PRICE),
    ],
  });
  const response = await rpc.request<{ value: number | null }>(
    "getFeeForMessage",
    [ethers.encodeBase64(message.bytes), { commitment: "confirmed" }],
  );
  const networkFee = lamports(response?.value, "the network fee");
  const accountRent =
    lamports(rent, "account rent") +
    (recoveryRent === null
      ? 0n
      : lamports(recoveryRent, "recovery account rent")) +
    (nullifierRent === null
      ? 0n
      : lamports(nullifierRent, "nullifier account rent"));
  return {
    networkFee,
    accountRent,
    buffer: FEE_BUFFER_LAMPORTS,
    reserve: networkFee + accountRent + FEE_BUFFER_LAMPORTS,
  };
}
